import loadable from "@loadable/component";
import { ConsoleLog, ModalProps } from "@inntechcorp/it-design";
import { GetLocalState } from "Connectivity/LocalStorage";
import ModalRootWrapper from "Modals/index";
import { BOContextPropsType } from "Context/types/BOContextPropsType";
import { AppModalData } from "Types/AppModalProps";

const Disconnect                    = loadable(() => import("Modals/disconnect/index") as Promise<any>);
const ExportData                    = loadable(() => import("Modals/exportdata/index") as Promise<any>);
const AffiliateUsersSettings        = loadable(() => import("Modals/settings/index") as Promise<any>);
const SetAuthenticationMethod       = loadable(() => import("Modals/setauthenticationmethod/index") as Promise<any>);

const getModalByName = (name:string) => {

  if ( name === "disconnect" )                    return Disconnect;
  if ( name === "exportData" )                    return ExportData;
  if ( name === "affiliateUsersSettings" )        return AffiliateUsersSettings;
  if ( name === "setAuthenticationMethod" )       return SetAuthenticationMethod;

  return null;

}

/**
 *
 * Modal'lar it-design'ın `useModal().showModal`'ı ile rahatlıkla açılabilir
 * fakat bu fonksiyon açılacak modal'ı config dosyasındaki değerlere göre kontrol
 * eder ve modal'ı bulursa modal'ın açılması için gerekli olan objeyi oluşturur.
 *
 * it-design 4.x: caller'a özel veriler (customProps) ve app context'i artık
 * `ModalProps.data` içinde taşınır; içerik daima prop'suz bir
 * `<ModalRootWrapper />`'dır (4.x, kendi ModalContentProps injection'ını yalnız
 * prop'suz content elementlerine yapar). Wrapper bu data'yı `useModalContent()`
 * ile açar.
 *
 * @param {*} name Açılacak modal'ın ismi (config'teki kayıt adı).
 * @param {*} data Modal chrome propları (width, butonlar...).
 * @param {*} customProps Modal içeriğine geçilecek app verisi.
 * @param {*} context BackOfficeContext değeri (portal içinde yeniden sağlanır).
 *
 */
const ModalData = (
  name          : string,
  data          : ModalProps,
  customProps   : Record<string, any>,
  context       : BOContextPropsType
) => {

  // Config'ten gelen modal dataları.
  const modals = GetLocalState("modals");

  if ( !modals ) {
    ConsoleLog.double("🔴 Inn.tech Back Office Error [Modal Data]", "Can't find the modals session state!", "error");
    return;
  }

  if ( !modals.hasOwnProperty(name) ) {
    ConsoleLog.double("🔴 Inn.tech Back Office Error [Modal Data]", "Can't find the modal in modals array, name: " + name, "error");
    return;
  }

  // for mobile
  if ( !data.hasOwnProperty("outerPadding") ) {
    data.outerPadding = "50px 0";
  }

  // Modal dışında tıklanıldığında kapatılır olup olmadığını belirler.
  if ( modals[name].hasOwnProperty("closeOnClickOutside") ) {
    data.closeOnClickOutside = modals[name].closeOnClickOutside;
  }

  // Disconnect modalı gibi ESC tuşu ile bile kapanmaması gereken
  // sabit modallar için ESC tuşu ile kapatmayı iptal etme.
  if ( modals[name].hasOwnProperty("preventESC") ) {
    data.preventESC = modals[name].preventESC;
  }

  // Modal'da OK tuşunu göstermez.
  if ( modals[name].hasOwnProperty("showOkButton") ) {
    data.showOkButton = modals[name].showOkButton;
  }

  // Modal'da Cancel tuşunu göstermez.
  if ( modals[name].hasOwnProperty("showCancelButton") ) {
    data.showCancelButton = modals[name].showCancelButton;
  }

  // Modal'da kapat tuşunu göstermez.
  if ( modals[name].hasOwnProperty("showCloseButton") ) {
    data.showCloseButton = modals[name].showCloseButton;
  }

  //
  if ( modals[name].hasOwnProperty("useRef") ) {
    data.useRef = modals[name].useRef;
  }

  // Bazı modal'lar extra custom proplara ihtiyaç duyabiliyor.
  // Config seviyesindeki props'ları caller'ınkilerle birleştirir.
  let mergedCustomProps = { ...customProps };
  if ( modals[name].hasOwnProperty("props") ) {
    mergedCustomProps = {
      ...mergedCustomProps,
      ...modals[name].props
    }
  }

  // Yüklenecek modal block'unun adresi.
  if ( !data.content ) {

    const modal = getModalByName(name);

    if ( !modal ) {
      throw new Error("INN Modal Error: There is no modal registered with this name '" + name + "'");
    }

    mergedCustomProps = {
      ...mergedCustomProps,
      modal: modal
    }

    data.content = <ModalRootWrapper />;

  }

  const appData: AppModalData = {
    customProps : mergedCustomProps,
    context     : context,
  };

  data.data = appData;

  return data;

}

export default ModalData;
